/**
 * Import brand from URL — API client.
 *
 * Owns endpoint resolution and the request contract for the skin/compose
 * endpoints. Error contract (the root-cause fix for the swallowed-typed-error
 * bug): typed application errors (422/409/401/…) are thrown immediately and
 * NEVER retried against another endpoint; only transport failures and 5xx
 * responses fall through to the next endpoint candidate.
 *
 * All requests go through authedFetch so the Bearer-token localStorage
 * fallback applies (cookie-stripped cross-site contexts).
 */

import { authedFetch } from '../../shared/authed-fetch.js';

export const LOCALHOST_COMPOSE_ENDPOINT = 'http://localhost:3001/api/compose-fast';

const TYPED_ERROR_CODES = new Set([
  'TEMPLATE_NOT_COMPOSABLE',
  'LOW_CONFIDENCE_NEEDS_USER_PICK',
  'CONTAMINATION_DETECTED',
  'PROFILE_INVALID',
  'RATE_LIMITED'
]);

function normalizeEndpoint(endpoint) {
  const raw = typeof endpoint === 'string' ? endpoint.trim() : '';
  if (!raw) return '';
  return raw.replace(/\/$/, '').replace(/\/api\/compose$/, '/api/compose-fast');
}

export function isLocalBuilder(win = window) {
  const hostname = win.location?.hostname || '';
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

function addCandidate(candidates, endpoint) {
  const normalized = normalizeEndpoint(endpoint);
  if (normalized && !candidates.includes(normalized)) candidates.push(normalized);
}

export function getComposeEndpointCandidates(win = window) {
  const candidates = [];
  addCandidate(candidates, win.__PB_COMPOSE_ENDPOINT);

  const protocol = win.location?.protocol;
  const origin = win.location?.origin;
  if ((protocol === 'http:' || protocol === 'https:') && origin) {
    addCandidate(candidates, `${origin}/api/compose-fast`);
  }

  if (isLocalBuilder(win) || candidates.length === 0) {
    addCandidate(candidates, LOCALHOST_COMPOSE_ENDPOINT);
  }
  return candidates;
}

export function isDeployedEndpoint(endpoint) {
  try {
    const url = new URL(endpoint);
    return (
      url.hostname !== 'localhost' &&
      url.hostname !== '127.0.0.1' &&
      url.hostname !== '0.0.0.0' &&
      url.hostname !== '::1' &&
      url.hostname !== '[::1]'
    );
  } catch {
    return false;
  }
}

/**
 * True when every endpoint candidate is a deployed (auth-gated) Worker —
 * i.e. there is no local proxy that would serve compose without a login.
 */
export function isDeployedOnly(win = window) {
  const candidates = getComposeEndpointCandidates(win);
  return candidates.length > 0 && candidates.every(isDeployedEndpoint);
}

export function endpointLabel(endpoint) {
  try {
    const parsed = new URL(endpoint, globalThis.window?.location?.origin);
    return parsed.host || parsed.pathname || endpoint;
  } catch {
    return endpoint;
  }
}

function makeTypedError(data, status) {
  const err = new Error(data?.message || data?.error || `HTTP ${status}`);
  err.code = data?.code;
  err.status = status;
  if (data?.code === 'TEMPLATE_NOT_COMPOSABLE') {
    err.templateSiteName = data.templateSiteName;
    err.availableTemplates = data.availableTemplates || [];
  } else if (data?.code === 'LOW_CONFIDENCE_NEEDS_USER_PICK') {
    err.industry = data.industry || 'general';
    err.candidates = data.candidates || [];
    err.stats = data.stats || null;
  } else if (data?.code === 'CONTAMINATION_DETECTED') {
    err.report = data.report || null;
  } else if (data?.code === 'PROFILE_INVALID') {
    err.errors = data.errors || [];
  }
  return err;
}

function statusForErrorCode(code) {
  if (code === 'AUTH_REQUIRED') return 401;
  if (code === 'LOW_CONFIDENCE_NEEDS_USER_PICK') return 409;
  if (
    code === 'PROFILE_INVALID' ||
    code === 'TEMPLATE_NOT_COMPOSABLE' ||
    code === 'CONTAMINATION_DETECTED'
  )
    return 422;
  if (code === 'RATE_LIMITED') return 429;
  if (code === 'INVALID_JSON' || code === 'INVALID_URL') return 400;
  return 500;
}

function makeAuthRequiredError(data) {
  const err = new Error(data?.message || 'Sign in to use Import brand from URL.');
  err.code = 'AUTH_REQUIRED';
  err.status = 401;
  return err;
}

/**
 * Classify a settled HTTP response. Returns:
 *   {result}          — success payload
 *   {throw: Error}    — terminal, do not try further endpoints
 *   {failure: string} — retryable against the next endpoint (5xx)
 */
function classifyResponse(resp, data, endpoint) {
  if (resp.ok && data?.state) return { result: data };

  if (resp.status === 401) {
    return { throw: makeAuthRequiredError(data) };
  }
  // Both the Worker's current lowercase `auth_required` and the planned
  // `AUTH_REQUIRED` map to the same typed code.
  const bodyCode = typeof data?.code === 'string' ? data.code : '';
  if (bodyCode.toUpperCase() === 'AUTH_REQUIRED') {
    return { throw: makeAuthRequiredError(data) };
  }
  if (TYPED_ERROR_CODES.has(bodyCode)) {
    return { throw: makeTypedError(data, resp.status) };
  }
  if (resp.status >= 400 && resp.status < 500) {
    // Client errors are terminal: the same bad input/credentials would fail
    // on every endpoint.
    const err = new Error(data?.error || data?.message || `HTTP ${resp.status}`);
    err.status = resp.status;
    return { throw: err };
  }
  return {
    failure: `${endpointLabel(endpoint)}: ${data?.error || data?.message || `HTTP ${resp.status}`}`
  };
}

/**
 * POST the compose request to the first endpoint that accepts it.
 *
 * @param {string} url                    Client site URL (already normalized).
 * @param {AbortSignal} [signal]
 * @param {{bundleId?: string, templateSiteName?: string}|null} [overrides]
 * @param {{doFetch?: typeof authedFetch, win?: Window}} [deps]  Test seams.
 */
export async function requestCompose(url, signal, overrides = null, deps = {}) {
  const doFetch = deps.doFetch || authedFetch;
  const endpoints = getComposeEndpointCandidates(deps.win || window);
  if (!endpoints.length) throw new Error('No compose endpoint configured');

  const failures = [];
  for (const endpoint of endpoints) {
    let resp;
    try {
      const requestBody = { url };
      if (overrides?.bundleId) requestBody.bundleId = overrides.bundleId;
      if (overrides?.templateSiteName) requestBody.templateSiteName = overrides.templateSiteName;
      resp = await doFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal
      });
    } catch (err) {
      if (err.name === 'AbortError' || signal?.aborted) throw err;
      // Transport failure (authedFetch wraps these in TemplateApiError) —
      // this endpoint is unreachable; try the next one.
      failures.push(`${endpointLabel(endpoint)}: ${err.message}`);
      continue;
    }

    const data = await resp.json().catch(() => null);
    const outcome = classifyResponse(resp, data, endpoint);
    if (outcome.result) return outcome.result;
    if (outcome.throw) throw outcome.throw;
    failures.push(outcome.failure);
  }

  throw new Error(`All compose endpoints failed (${failures.join('; ')})`);
}

// ---------------------------------------------------------------------------
// Two-phase API (/api/skin/v1/*) with NDJSON progress streaming
// ---------------------------------------------------------------------------

/** Human-readable labels for streamed progress phases, in pipeline order. */
export const PHASE_LABELS = Object.freeze({
  scraping: 'Scraping site',
  profiling: 'Extracting brand profile',
  routing: 'Choosing template',
  'loading-bundle': 'Loading template',
  skinning: 'Skinning template',
  assembling: 'Assembling pages',
  scanning: 'Checking for leftover template content',
  validating: 'Finishing'
});
export const PHASE_ORDER = Object.freeze(Object.keys(PHASE_LABELS));

function skinEndpointCandidates(pathSuffix, win = window) {
  return getComposeEndpointCandidates(win).map((endpoint) =>
    endpoint.replace(/\/api\/compose-fast$/, `/api/skin/v1/${pathSuffix}`)
  );
}

/**
 * Parse an NDJSON body into an async sequence of frames. Exported for tests
 * (feed it a hand-built Response/ReadableStream).
 */
export async function* readNdjsonEvents(resp) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline;
    while ((newline = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line) yield JSON.parse(line);
    }
  }
  const tail = (buffer + decoder.decode()).trim();
  if (tail) yield JSON.parse(tail);
}

// Sticky per-session flag: once every candidate 404s the v1 routes, fall back
// to single-shot /api/compose-fast (compat mode) without re-probing.
let _compatMode = false;
export function isCompatMode() {
  return _compatMode;
}
export function _resetCompatModeForTests() {
  _compatMode = false;
}

/**
 * POST to the first skin/v1 endpoint that accepts the request, streaming
 * progress frames into onProgress. Resolves with the result payload.
 * Throws typed errors identical to requestCompose's contract; a 404/405 on
 * EVERY candidate throws err.code='SKIN_V1_UNAVAILABLE' (compat-mode signal).
 */
async function requestSkinV1(pathSuffix, body, { signal, onProgress, deps = {} } = {}) {
  const doFetch = deps.doFetch || authedFetch;
  const endpoints = skinEndpointCandidates(pathSuffix, deps.win || window);
  if (!endpoints.length) throw new Error('No skin endpoint configured');

  const failures = [];
  let sawOnlyMissingRoutes = true;

  for (const endpoint of endpoints) {
    let resp;
    try {
      resp = await doFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' },
        body: JSON.stringify(body),
        signal
      });
    } catch (err) {
      if (err.name === 'AbortError' || signal?.aborted) throw err;
      failures.push(`${endpointLabel(endpoint)}: ${err.message}`);
      continue;
    }

    if (resp.status === 404 || resp.status === 405) {
      failures.push(`${endpointLabel(endpoint)}: HTTP ${resp.status}`);
      continue;
    }
    sawOnlyMissingRoutes = false;

    const contentType = resp.headers?.get?.('content-type') || '';
    if (contentType.includes('application/x-ndjson') && resp.ok && resp.body) {
      let result = null;
      let errorFrame = null;
      for await (const frame of readNdjsonEvents(resp)) {
        if (frame.type === 'progress') onProgress?.(frame.phase, frame.detail || {});
        else if (frame.type === 'result') result = frame.payload;
        else if (frame.type === 'error') errorFrame = frame;
      }
      if (result) return result;
      if (errorFrame) {
        const status = statusForErrorCode(errorFrame.code);
        if (status === 401) throw makeAuthRequiredError(errorFrame);
        throw makeTypedError(errorFrame, status);
      }
      throw new Error('Skin stream ended without a result');
    }

    // Non-streamed path (plain JSON success or error body).
    const data = await resp.json().catch(() => null);
    if (resp.ok && data && !data.code) {
      onProgress?.('working', { indeterminate: true });
      return data;
    }
    const outcome = classifyResponse({ ok: false, status: resp.status }, data, endpoint);
    if (outcome.throw) throw outcome.throw;
    failures.push(outcome.failure || `${endpointLabel(endpoint)}: HTTP ${resp.status}`);
  }

  if (sawOnlyMissingRoutes) {
    _compatMode = true;
    const err = new Error('Two-phase skin API not deployed on any endpoint.');
    err.code = 'SKIN_V1_UNAVAILABLE';
    throw err;
  }
  throw new Error(`All skin endpoints failed (${failures.join('; ')})`);
}

/**
 * Phase 1: scrape + extract. Returns {outcome, profile, industry,
 * templateCandidates[{id, displayName, sourceDomain, primary}], scrapeReport, cached}.
 */
export function fetchSkinProfile(body, opts = {}) {
  return requestSkinV1('profile', body, opts);
}

/**
 * Phase 2: skin a (possibly edited) profile. Returns {state, stats,
 * importReport, logoAsset?}.
 */
export function fetchSkinApply(body, opts = {}) {
  return requestSkinV1('apply', body, opts);
}
